/**
 * Role-gated rendering helpers. Wraps destructive / owner-only actions so
 * support + read-only admins see disabled state with explanatory tooltip
 * instead of raw buttons they can't use. Server still enforces.
 *
 * @copyright 2025 Glimbr Pty Ltd
 * @version 2026-04-19-v1
 */
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { useAdminSession, canPerform } from '../../lib/auth';
import type { AdminRole } from '../../lib/types';

type Scope = 'owner_only' | 'support_ok' | 'read';

export function useCan(scope: Scope): boolean {
    const session = useAdminSession();
    return canPerform(session.role, scope);
}

export function RoleGate({
    scope,
    children,
    fallback = null
}: {
    scope: Scope;
    children: ReactNode;
    fallback?: ReactNode;
}) {
    const ok = useCan(scope);
    return <>{ok ? children : fallback}</>;
}

interface RoleButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    scope: Scope;
    children: ReactNode;
}

export function RoleButton({ scope, children, title, disabled, ...rest }: RoleButtonProps) {
    const ok = useCan(scope);
    return (
        <button {...rest} disabled={disabled || !ok} title={!ok ? 'Requires elevated role' : title}>
            {children}
        </button>
    );
}

export const SCOPE_BY_ROLE: Record<AdminRole, Scope[]> = {
    owner: ['read', 'support_ok', 'owner_only'],
    support: ['read', 'support_ok'],
    read_only: ['read']
};
